import { useState, useEffect, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import MetricCard from "./widgets/MetricCard";
import { getCustomWidget } from "../custom/index";
import ChartWidget from "./widgets/ChartWidget";
import TableWidget from "./widgets/TableWidget";
import ContainerWidget from "./widgets/ContainerWidget";
// import RelationshipTableWidget from './widgets/RelationshipTableWidget';
import ListWidget from "./widgets/ListWidget";
import TextWidget from "./widgets/TextWidget";
import Divider from "./widgets/Divider";
import ButtonWidget from "./widgets/ButtonWidget";
import KanbanWidget from "./widgets/KanbanWidget";
import FilterWidget from "./widgets/FilterWidget";
import CalendarWidget from "./widgets/CalendarWidget";
import GanttWidget from "./widgets/GanttWidget";
import FormsManagerWidget from "./widgets/FormsManagerWidget";
import RowEditModal from "./builder/RowEditModal";
import { Card, CardContent } from "./ui/card";
import { fetchSheetData, fetchSheetNames, extractSheetId } from "../services/google-sheets.service";
import { googleSheetsCache } from "../stores/google-sheets-cache";
import { pageApi } from "../services/page.service";
import { useIntegrationsStore } from "../stores/integrations.store";
import { integrationsApi } from "../services/integrations.service";
import { useWorkspaceStore } from "../stores/workspace.store";
import { useWorkspaceFilterGroupsStore } from "../stores/workspace-filter-groups.store";

/**
 * Infere o tipo de uma coluna analisando os valores de amostra das linhas.
 * Retorna 'number', 'date' ou 'text'.
 */
function inferColumnType(fieldKey: string, rows: any[]): string {
  const sampleSize = Math.min(20, rows.length);
  const samples = rows.slice(0, sampleSize);

  let numberCount = 0;
  let dateCount = 0;
  let validSamples = 0;

  for (const row of samples) {
    const value = row[fieldKey];
    if (value === null || value === undefined || value === '') continue;
    validSamples++;

    const stringValue = String(value).trim();
    const hasDateSeparators = /[\/-]/.test(stringValue);

    if (
      (typeof value === 'number' ||
        (!isNaN(Number(stringValue)) && stringValue !== '' && !hasDateSeparators))
    ) {
      numberCount++;
      continue;
    }

    const datePatterns = [
      /^\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4}$/,
      /^\d{4}[\/\-\.]\d{1,2}[\/\-\.]\d{1,2}$/,
    ];

    if (datePatterns.some((p) => p.test(stringValue))) {
      const parts = stringValue.split(/[\/\-\.]/);
      if (parts.length === 3) {
        const d = parts[0].length === 4
          ? new Date(+parts[0], +parts[1] - 1, +parts[2])
          : new Date(+parts[2], +parts[1] - 1, +parts[0]);
        if (!isNaN(d.getTime())) dateCount++;
      }
    }
  }

  if (validSamples === 0) return 'text';
  if (numberCount / validSamples >= 0.8) return 'number';
  if (dateCount / validSamples >= 0.8) return 'date';
  return 'text';
}

interface ComponentRendererProps {
  component: any;
  allComponents?: any[];
  onButtonClick?: (componentId: string) => void;
  onComponentChange?: (componentId: string, config: any) => void;
  activeFilters?: Record<string, string>;
  onFilterChange?: (field: string, value: string) => void;
  isCompact?: boolean;
  // Callbacks do builder — usados apenas pelo container para gerenciar filhos.
  isBuilder?: boolean;
  onEditComponent?: (component: any) => void;
  onDeleteComponent?: (componentId: string) => void;
  onDataEditClick?: (componentId: string) => void;
  onColumnsEditClick?: (componentId: string) => void;
  onChartFieldsEditClick?: (componentId: string) => void;
  onKanbanColumnsEditClick?: (componentId: string) => void;
  onAddChild?: (containerId: string, child: any) => void;
  onMoveComponent?: (componentId: string, targetContainerId: string) => void;
}

export default function ComponentRenderer({
  component,
  allComponents = [],
  onButtonClick,
  onComponentChange,
  activeFilters = {},
  onFilterChange,
  isCompact = false,
  isBuilder = false,
  onEditComponent,
  onDeleteComponent,
  onDataEditClick,
  onColumnsEditClick,
  onChartFieldsEditClick,
  onKanbanColumnsEditClick,
  onAddChild,
  onMoveComponent,
}: ComponentRendererProps) {
  const { getById: getIntegrationById, addIntegration } = useIntegrationsStore();
  const queryClient = useQueryClient();
  const { slug: currentPageSlug } = useParams<{ slug: string }>();
  const { currentWorkspace } = useWorkspaceStore();
  const { getById: getFilterGroupById, filterGroups } = useWorkspaceFilterGroupsStore();

  // Resolve spreadsheetId from integrationId (or fall back to legacy direct value)
  const resolvedSpreadsheetId: string | undefined =
    component.config?.googleSheets?.integrationId
      ? (getIntegrationById(component.config.googleSheets.integrationId)?.config?.spreadsheetId as string | undefined)
      : component.config?.googleSheets?.spreadsheetId;

  // Resolve calendarId from integrationId (or fall back to legacy direct value)
  const resolvedCalendarId: string | undefined =
    component.config?.googleCalendar?.integrationId
      ? (getIntegrationById(component.config.googleCalendar.integrationId)?.config?.calendarId as string | undefined)
      : component.config?.googleCalendar?.calendarId;

  const [editingRow, setEditingRow] = useState<{
    row: any;
    index: number;
    componentId: string;
    isGoogleSheets?: boolean;
  } | null>(null);
  
  const [googleSheetsData, setGoogleSheetsData] = useState<any[]>([]);
  const [googleSheetsLoading, setGoogleSheetsLoading] = useState(false);
  const [googleSheetsError, setGoogleSheetsError] = useState<string | null>(null);
  const [restApiData, setRestApiData] = useState<any[]>([]);
  const [restApiLoading, setRestApiLoading] = useState(false);
  const [restApiError, setRestApiError] = useState<string | null>(null);

  // Busca os dados da API REST e os aplica.
  // - persist=false: modo ao vivo, guarda apenas em estado local (restApiData).
  // - persist=true: modo "salvar no banco", grava os dados em config.dataSource.data
  //   via onComponentChange e marca a data da sincronização (restApi.lastSyncedAt).
  const fetchAndApplyRestApi = useCallback(
    async (opts: { persist: boolean }) => {
      const restApi = component.config?.restApi;
      if (!restApi?.integrationId || !currentWorkspace) return;

      setRestApiLoading(true);
      setRestApiError(null);

      try {
        const raw = await integrationsApi.fetchRestApi(
          currentWorkspace.id,
          restApi.integrationId,
        );

        let resolved: any = raw;
        const path: string = restApi.responsePath || '';
        if (path) {
          for (const key of path.split('.')) {
            resolved = resolved?.[key];
          }
        }

        let arr: any[] = [];
        if (Array.isArray(resolved)) {
          arr = resolved;
        } else if (resolved && typeof resolved === 'object') {
          // Tenta encontrar o primeiro campo que seja array
          const found = Object.values(resolved).find((v) => Array.isArray(v));
          arr = Array.isArray(found) ? found : [];
        }

        // Auto-detecta colunas quando ainda não há nenhuma configurada
        let nextColumns = component.config.columns;
        if (
          arr.length > 0 &&
          (!component.config.columns || component.config.columns.length === 0)
        ) {
          nextColumns = Object.keys(arr[0]).map((k) => ({
            field: k,
            label: k,
            type: inferColumnType(k, arr),
            visible: true,
          }));
        }

        if (opts.persist) {
          // Modo "salvar no banco": persiste dados + colunas + marcador de sync.
          if (onComponentChange) {
            onComponentChange(component.id, {
              ...component.config,
              columns: nextColumns,
              dataSource: {
                type: 'static',
                ...(component.config.dataSource || {}),
                data: arr,
              },
              restApi: {
                ...restApi,
                lastSyncedAt: new Date().toISOString(),
              },
            });
          }
          // Em modo persistido a tabela lê de dataSource.data, não do estado local.
          setRestApiData([]);
        } else {
          // Modo ao vivo: mantém apenas em estado local.
          if (nextColumns !== component.config.columns && onComponentChange) {
            onComponentChange(component.id, {
              ...component.config,
              columns: nextColumns,
            });
          }
          setRestApiData(arr);
        }
      } catch (err: any) {
        console.error('Erro ao buscar REST API:', err);
        setRestApiError(
          err?.response?.data?.message ?? err.message ?? 'Erro ao buscar API',
        );
        if (!opts.persist) setRestApiData([]);
      } finally {
        setRestApiLoading(false);
      }
    },
    [component.config, component.id, currentWorkspace?.id, onComponentChange],
  );

  // Decide quando buscar a API REST conforme a configuração do componente.
  useEffect(() => {
    const restApi = component.config?.restApi;
    if (
      component.type !== 'table' ||
      !restApi?.enabled ||
      !restApi?.integrationId ||
      !currentWorkspace
    ) {
      setRestApiData([]);
      return;
    }

    if (restApi.persistToDatabase) {
      // Modo "salvar no banco": busca só na primeira vez (sem dados salvos).
      // Atualizações posteriores acontecem pelo botão "Sincronizar".
      setRestApiData([]);
      const hasSavedData = (component.config?.dataSource?.data?.length ?? 0) > 0;
      if (!hasSavedData) {
        fetchAndApplyRestApi({ persist: true });
      }
      return;
    }

    // Modo ao vivo: busca a cada carregamento.
    fetchAndApplyRestApi({ persist: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    component.config?.restApi?.integrationId,
    component.config?.restApi?.enabled,
    component.config?.restApi?.responsePath,
    component.config?.restApi?.persistToDatabase,
    currentWorkspace?.id,
  ]);

  // Buscar dados do Google Sheets quando configurado
  useEffect(() => {
    const loadGoogleSheetsData = async () => {
      const googleSheets = component.config?.googleSheets;
      
      if (!resolvedSpreadsheetId || !googleSheets?.enabled) {
        setGoogleSheetsData([]);
        return;
      }

      // Verificar se já existe no cache
      const cached = googleSheetsCache.get(
        resolvedSpreadsheetId,
        googleSheets.range || 'Sheet1'
      );

      if (cached) {
        setGoogleSheetsData(cached.data);
        
        // Atualizar colunas se necessário
        if (cached.headers.length > 0 && onComponentChange) {
          const autoColumns = cached.headers.map((header: string) => {
            const field = header.toLowerCase().replace(/\s+/g, '_');
            return {
              field,
              label: header,
              type: inferColumnType(field, cached.data),
              visible: true,
            };
          });
          
          // Apenas atualizar colunas, NÃO salvar data no banco
          if (!component.config.columns || component.config.columns.length === 0) {
            onComponentChange(component.id, {
              ...component.config,
              columns: autoColumns,
            });
          }
        }
        return;
      }

      setGoogleSheetsLoading(true);
      setGoogleSheetsError(null);
      
      try {
        const { rows, headers } = await fetchSheetData(
          resolvedSpreadsheetId,
          googleSheets.range || 'Sheet1'
        );
        
        
        // Armazenar no cache compartilhado
        googleSheetsCache.set(
          resolvedSpreadsheetId,
          googleSheets.range || 'Sheet1',
          rows,
          headers
        );
        
        setGoogleSheetsData(rows);
        
        // Quando Google Sheets está ativo, criar colunas baseadas nos headers
        if (headers.length > 0 && googleSheets.enabled) {
          const autoColumns = headers.map((header: string) => {
            const field = header.toLowerCase().replace(/\s+/g, '_');
            return {
              field,
              label: header,
              type: inferColumnType(field, rows),
              visible: true,
            };
          });
          
          if (onComponentChange) {
            // Apenas atualizar colunas, NÃO salvar data no banco
            onComponentChange(component.id, {
              ...component.config,
              columns: autoColumns,
            });
          }
        }
      } catch (error: any) {
        console.error('Erro ao buscar Google Sheets:', error);
        setGoogleSheetsError(error.message || 'Erro ao carregar dados do Google Sheets');
        setGoogleSheetsData([]);
      } finally {
        setGoogleSheetsLoading(false);
      }
    };

    if (component.type === 'table') {
      loadGoogleSheetsData();
    }

    // Subscrever para atualizações do cache
    const googleSheets = component.config?.googleSheets;
    if (resolvedSpreadsheetId && googleSheets?.enabled) {
      const unsubscribe = googleSheetsCache.subscribe(
        resolvedSpreadsheetId,
        googleSheets.range || 'Sheet1',
        (data) => {
          setGoogleSheetsData(data);
        }
      );

      return () => unsubscribe();
    }
  }, [resolvedSpreadsheetId, component.config?.googleSheets?.range, component.config?.googleSheets?.enabled]);

  // Helper to resolve spreadsheetId for any component (supports integrationId or legacy direct)
  const resolveCompSpreadsheetId = (comp: any): string | undefined => {
    const gs = comp?.config?.googleSheets;
    if (!gs) return undefined;
    if (gs.integrationId) {
      return getIntegrationById(gs.integrationId)?.config?.spreadsheetId as string | undefined;
    }
    return gs.spreadsheetId;
  };

  // Campos de filtros do tipo "Tabela Vinculada" (seletor de tabela, não filtram linhas)
  const linkedTableFilterFields = useMemo(
    () =>
      new Set(
        allComponents
          .filter(
            (c) =>
              c.type === "filter" &&
              c.config?.optionsSource === "linkedTable" &&
              c.config?.field,
          )
          .map((c) => c.config.field),
      ),
    [allComponents],
  );

  // Resolve o componente fonte de dados de um metric-card/chart/table/list,
  // considerando tanto sourceComponentId fixo quanto dynamicSourceField
  // (fonte dinâmica via filtro "Tabela Vinculada"). dynamicSourceField tem prioridade.
  const resolveDynamicSourceComponent = (cfg: any) => {
    if (cfg?.dynamicSourceField) {
      const dynamicId = activeFilters[cfg.dynamicSourceField];
      return dynamicId ? allComponents.find((c) => c.id === dynamicId) : undefined;
    }
    if (cfg?.sourceComponentId) {
      return allComponents.find((c) => c.id === cfg.sourceComponentId);
    }
    return undefined;
  };

  // Extrai o array de dados cru (antes de applyFilters) de um componente fonte já resolvido.
  const extractSourceData = (sourceComponent: any, cfg: any): any[] => {
    if (!sourceComponent) return [];
    if (sourceComponent.config?.googleSheets?.enabled) {
      const cached = googleSheetsCache.get(
        resolveCompSpreadsheetId(sourceComponent) || '',
        sourceComponent.config.googleSheets.range || 'Sheet1',
      );
      return cached ? cached.data : [];
    }
    if (sourceComponent.type === "kanban" && sourceComponent.config?.columns) {
      return sourceComponent.config.columns.flatMap((column: any) =>
        (column.cards || [])
          .map((card: any) => ({ ...card, columnId: column.id, columnTitle: column.title }))
          .filter((card: any) =>
            cfg?.kanbanFilterField && cfg?.kanbanFilterValue
              ? String(card[cfg.kanbanFilterField]) === String(cfg.kanbanFilterValue)
              : true,
          ),
      );
    }
    return sourceComponent.config?.dataSource?.data || [];
  };

  // Resolve as opções de um filtro dos modos "table"/"linkedTable" a partir dos
  // dados ATUAIS da tabela fonte, em vez do snapshot salvo em config.options
  // (que só era atualizado quando alguém abria a config do filtro e clicava em
  // "Atualizar"). Retorna undefined quando não há dados disponíveis ainda, para
  // cair de volta no snapshot persistido em vez de esvaziar o dropdown.
  const resolveTableBasedFilterOptions = (
    cfg: any,
  ): { label: string; value: string }[] | undefined => {
    if (cfg?.optionsSource !== "table" && cfg?.optionsSource !== "linkedTable") return undefined;
    // Agrupamento manual depende de curadoria do usuário (quais valores pertencem a
    // cada grupo) — continua exigindo "Atualizar" na config para não perder os grupos.
    if (cfg?.optionsSource === "table" && cfg?.enableManualGrouping) return undefined;
    if (!cfg?.sourceTableId || !cfg?.field) return undefined;

    const sourceTable = allComponents.find(
      (c) => c.id === cfg.sourceTableId && c.type === "table",
    );
    if (!sourceTable) return undefined;

    let rows: any[] = sourceTable.config?.dataSource?.data || [];
    if (rows.length === 0 && sourceTable.config?.googleSheets?.enabled) {
      const spreadsheetId = resolveCompSpreadsheetId(sourceTable);
      const cached = spreadsheetId
        ? googleSheetsCache.get(spreadsheetId, sourceTable.config.googleSheets.range || "Sheet1")
        : null;
      if (cached) rows = cached.data;
    }
    if (rows.length === 0) return undefined;

    if (cfg.optionsSource === "linkedTable") {
      const results: { label: string; value: string }[] = [];
      for (const row of rows) {
        const linkVal = row[cfg.field];
        if (!linkVal || typeof linkVal !== "object" || !linkVal.pageSlug) continue;
        const childTable = allComponents.find(
          (c) => c.type === "table" && (c as any)._pageSlug === linkVal.pageSlug,
        );
        if (!childTable) continue;
        results.push({ label: linkVal.name || childTable.id, value: childTable.id });
      }
      return results;
    }

    const columnDef = (sourceTable.config?.columns || []).find(
      (c: any) => c.field === cfg.field,
    );
    const isTableLink = columnDef?.type === "tableLink";
    const rawValues = rows
      .map((r: any) => r[cfg.field])
      .filter((v: any) => v !== null && v !== undefined && v !== "");

    if (isTableLink) {
      const names = [
        ...new Set(
          rawValues
            .map((v: any) => (typeof v === "object" ? v.name : String(v)))
            .filter((name: any) => !!name),
        ),
      ] as string[];
      return names.map((name) => ({ label: name, value: name }));
    }

    const unique = [...new Set(rawValues)] as any[];
    return unique.map((v) => ({ label: String(v), value: String(v) }));
  };

  const parseDate = (dateValue: any): Date | null => {
    if (!dateValue) return null;

    let date: Date;

    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === "string") {
      // Tenta formatos: dd/mm/yyyy, dd-mm-yyyy, yyyy-mm-dd
      const parts = dateValue.split(/[\/\-\.]/);
      if (parts.length === 3) {
        // Se começa com 4 dígitos, é yyyy-mm-dd
        if (parts[0].length === 4) {
          date = new Date(
            parseInt(parts[0]),
            parseInt(parts[1]) - 1,
            parseInt(parts[2]),
          );
        } else {
          // Formato brasileiro dd/mm/yyyy
          date = new Date(
            parseInt(parts[2]),
            parseInt(parts[1]) - 1,
            parseInt(parts[0]),
          );
        }
      } else {
        date = new Date(dateValue);
      }
    } else if (typeof dateValue === "number") {
      // Excel serial date
      date = new Date((dateValue - 25569) * 86400 * 1000);
    } else {
      return null;
    }

    return isNaN(date.getTime()) ? null : date;
  };

  // Detectar se existe um filtro temporal na página com agrupamento ativo
  const detectTemporalFilter = (): {
    type: "mes" | "semestre" | "trimestre";
    enableGrouping: boolean;
  } | null => {
    const filterComponent = allComponents.find((c) => c.type === "filter");
    if (!filterComponent) return null;

    const preset = filterComponent.config?.preset;
    // Se enableGrouping não está definido (undefined), usar false. Se está definido, usar o valor
    const enableGrouping = filterComponent.config?.enableGrouping ?? false;

    let type: "mes" | "semestre" | "trimestre" | null = null;
    if (preset === "monthly") type = "mes";
    if (preset === "semester") type = "semestre";
    if (preset === "quarterly") type = "trimestre";

    return type ? { type, enableGrouping } : null;
  };

  const applyFilters = (data: any[], shouldGroup = false) => {
    if (!data || data.length === 0) return data;

    let filtered = data;
    const temporalFilterConfig = detectTemporalFilter();

    // Aplicar filtros ativos
    Object.entries(activeFilters).forEach(([field, value]) => {
      if (!value || value === "") return;

      // Filtro do tipo "Tabela Vinculada": o valor é um ID de componente usado
      // para resolver a fonte dinâmica de métricas/gráficos, não filtra linhas.
      if (linkedTableFilterFields.has(field)) return;

      filtered = filtered.filter((item) => {
        // Para filtros de data (mes, semestre, trimestre), extrair da coluna 'data'
        if (field === "mes" || field === "semestre" || field === "trimestre") {
          //hasActiveTemporalFilter = true;

          const dateValue =
            item["data"] || item["Data"] || item["DATE"] || item["date"];
          const date = parseDate(dateValue);
          if (!date) return false;

          const month = date.getMonth() + 1; // 1-12

          if (field === "mes") {
            return month.toString().padStart(2, "0") === value;
          } else if (field === "semestre") {
            const semester = month <= 6 ? "1" : "2";
            return semester === value;
          } else if (field === "trimestre") {
            const quarter = Math.ceil(month / 3).toString();
            return quarter === value;
          }
        } else if (field === "periodo") {
          // Filtro de período: value é um JSON {"start":"yyyy-MM-dd","end":"yyyy-MM-dd"}
          try {
            const periodFilter = JSON.parse(value);
            const startDate = periodFilter.start ? new Date(periodFilter.start) : null;
            const endDate = periodFilter.end ? new Date(periodFilter.end) : null;

            if (!startDate && !endDate) return true; // Sem filtro

            const dateValue =
              item["data"] || item["Data"] || item["DATE"] || item["date"];
            const itemDate = parseDate(dateValue);
            if (!itemDate) return false;

            // Verificar se a data do item está dentro do período
            if (startDate && itemDate < startDate) return false;
            if (endDate && itemDate > endDate) return false;
            return true;
          } catch (e) {
            // Se não conseguir parsear o JSON, ignorar o filtro
            return true;
          }
        } else {
          const rawItemValue = item[field];
          // Colunas tableLink guardam um objeto { name, sheetsUrl, pageSlug };
          // o nome da página é o valor comparável para fins de filtro.
          const itemValue =
            rawItemValue && typeof rawItemValue === "object"
              ? rawItemValue.name?.toString()
              : rawItemValue?.toString();
          if (!itemValue) return false;

          // Verificar primeiro se há um grupo de filtro compartilhado que mapeia este campo
          // (não depende de allComponents estar atualizado)
          const sharedFg = filterGroups.find(
            (fg) =>
              (fg.field === field || fg.dataSource?.sourceColumn === field) &&
              (fg.groups as { id: string; name: string; values: string[] }[]).some(
                (g) => g.name === value
              )
          );

          if (sharedFg) {
            const group = (sharedFg.groups as { id: string; name: string; values: string[] }[]).find(
              (g) => g.name === value
            );
            return group ? group.values.includes(itemValue) : false;
          }

          // Fallback: buscar componente de filtro para agrupamento manual
          const filterComp = allComponents.find(
            (c) => c.type === "filter" && c.config?.preset === "custom" && c.config?.field === field
          );

          // Modo shared via filterComp (quando o store ainda não está populado)
          if (filterComp?.config?.optionsSource === "shared" && filterComp?.config?.sharedFilterGroupId) {
            const fg = getFilterGroupById(filterComp.config.sharedFilterGroupId);
            if (fg) {
              const group = (fg.groups as { id: string; name: string; values: string[] }[]).find(
                (g) => g.name === value
              );
              return group ? group.values.includes(itemValue) : false;
            }
            return false;
          }

          // Modo agrupamento manual por tabela
          const opts: { label: string; value: string; values?: string[] }[] =
            filterComp?.config?.options || [];
          const selectedOpt = opts.find(
            (o) => (typeof o === "string" ? o : o.value) === value
          );

          if (selectedOpt && typeof selectedOpt !== "string" && selectedOpt.values?.length) {
            return selectedOpt.values.includes(itemValue);
          }

          return itemValue === value;
        }

        return false;
      });
    });

    // Se é um gráfico e tem filtro temporal com agrupamento ativo, agrupar dados por período
    if (
      shouldGroup &&
      temporalFilterConfig &&
      temporalFilterConfig.enableGrouping &&
      filtered.length > 0
    ) {
      const monthNames = [
        "Jan",
        "Fev",
        "Mar",
        "Abr",
        "Mai",
        "Jun",
        "Jul",
        "Ago",
        "Set",
        "Out",
        "Nov",
        "Dez",
      ];
      const grouped = new Map<string, any>();

      filtered.forEach((item) => {
        const dateValue =
          item["data"] || item["Data"] || item["DATE"] || item["date"];
        const date = parseDate(dateValue);
        if (!date) return;

        let groupKey: string;
        if (temporalFilterConfig.type === "mes") {
          const month = date.getMonth();
          groupKey = monthNames[month];
        } else if (temporalFilterConfig.type === "semestre") {
          const semester = date.getMonth() < 6 ? 1 : 2;
          groupKey = `${semester}º Sem`;
        } else if (temporalFilterConfig.type === "trimestre") {
          const quarter = Math.ceil((date.getMonth() + 1) / 3);
          groupKey = `${quarter}º Tri`;
        } else {
          groupKey = dateValue.toString();
        }

        if (!grouped.has(groupKey)) {
          grouped.set(groupKey, {
            ...item,
            data: groupKey,
            _count: 0,
            _values: {},
          });
        }

        const groupItem = grouped.get(groupKey)!;
        groupItem._count++;

        // Agregar valores numéricos
        Object.keys(item).forEach((key) => {
          if (
            key !== "data" &&
            key !== "Data" &&
            key !== "DATE" &&
            key !== "date"
          ) {
            const value = parseFloat(item[key]);
            if (!isNaN(value)) {
              if (!groupItem._values[key]) {
                groupItem._values[key] = [];
              }
              groupItem._values[key].push(value);
            }
          }
        });
      });

      // Calcular agregações (soma para valores)
      const result = Array.from(grouped.values()).map((item) => {
        const aggregated: any = { data: item.data };
        Object.keys(item._values).forEach((key) => {
          const values = item._values[key];
          aggregated[key] = values.reduce((a: number, b: number) => a + b, 0);
        });
        return aggregated;
      });

      return result;
    }

    return filtered;
  };

  const getComponentData = () => {
    const { config } = component;

    // Se tem sourceComponentId (fixo) ou dynamicSourceField (via filtro "Tabela
    // Vinculada"), buscar dados do componente referenciado/resolvido
    let data = [];
    if (config?.sourceComponentId || config?.dynamicSourceField) {
      const sourceComponent = resolveDynamicSourceComponent(config);
      data = extractSourceData(sourceComponent, config);
    } else {
      // Caso contrário, usar dados próprios
      // Se este componente usa Google Sheets, buscar do cache
      if (config?.googleSheets?.enabled) {
        const cached = googleSheetsCache.get(
          resolvedSpreadsheetId || '',
          config.googleSheets.range || 'Sheet1'
        );
        if (cached) {
          data = cached.data;
        }
      } else {
        data = config?.dataSource?.data || [];
      }
    }

    let processedData = data;

    // Se deve agrupar duplicados e tem xKey/yKey configurados
    if (
      config?.groupDuplicates &&
      config?.xKey &&
      config?.yKey &&
      data.length > 0
    ) {
      const grouped = data.reduce((acc: any, item: any) => {
        const key = item[config.xKey];
        const value = Number(item[config.yKey]) || 0;

        if (acc[key]) {
          acc[key] += value;
        } else {
          acc[key] = value;
        }

        return acc;
      }, {});

      processedData = Object.entries(grouped).map(([key, value]) => ({
        [config.xKey]: key,
        [config.yKey]: value,
      }));
    }

    // Aplicar ordenação se configurada
    if (config?.sortOrder && config?.yKey && processedData.length > 0) {
      processedData = [...processedData].sort((a, b) => {
        const aValue = Number(a[config.yKey]) || 0;
        const bValue = Number(b[config.yKey]) || 0;
        return config.sortOrder === "desc" ? bValue - aValue : aValue - bValue;
      });
    }

    // Aplicar limite se configurado
    if (config?.limit && config.limit > 0 && processedData.length > 0) {
      processedData = processedData.slice(0, config.limit);
    }

    // Aplicar filtros mestres
    processedData = applyFilters(processedData);

    return processedData;
  };

  const renderComponent = () => {
    const { type, config } = component;

    switch (type) {
      case "metric-card":
        let metricValue = config?.value || 0;

        // Se tem sourceComponentId (fixo) ou dynamicSourceField (via filtro
        // "Tabela Vinculada"), calcular valor da tabela resolvida
        if (config?.sourceComponentId || config?.dynamicSourceField) {
          const sourceComponent = resolveDynamicSourceComponent(config);
          let data = extractSourceData(sourceComponent, config);

          // Aplicar filtros aos dados do componente fonte
          data = applyFilters(data);

          // Aplicar filtro customizado da métrica (se configurado)
          if (config.filterField && config.filterValue) {
            data = data.filter((item: any) => 
              String(item[config.filterField]) === String(config.filterValue)
            );
          }

          const aggregationType = config.aggregationType || "sum";
          const aggregationField = config.aggregationField;

          if (aggregationType === "count") {
            metricValue = data.length == null ? 0 : data.length;
          } else if (aggregationField && data.length > 0) {
            const values = data
              .map((item: any) => Number(item[aggregationField]) || 0)
              .filter((val: number) => !isNaN(val));

            if (values.length > 0) {
              switch (aggregationType) {
                case "sum":
                  metricValue = values.reduce(
                    (acc: number, val: number) => acc + val,
                    0,
                  );
                  break;
                case "avg":
                  metricValue =
                    values.reduce((acc: number, val: number) => acc + val, 0) /
                    values.length;
                  break;
                case "min":
                  metricValue = Math.min(...values);
                  break;
                case "max":
                  metricValue = Math.max(...values);
                  break;
              }
            }
          }
        }

        return (
          <MetricCard
            title={config?.title || "Métrica"}
            value={metricValue}
            icon={config?.icon}
            format={config?.format}
            trend={config?.trend}
            style={config?.style}
          />
        );

      case "chart":
        const chartData = applyFilters(getComponentData(), true); // true = agrupar por período temporal
        return (
          <ChartWidget
            title={config?.title || "Gráfico"}
            type={config?.chartType || "line"}
            data={chartData}
            xKey={config?.xKey}
            yKey={config?.yKey}
            colors={config?.colors}
          />
        );

      case "table":
        // Prioridade de dados: REST API > Google Sheets > Component Source > Manual Data
        // Em modo "salvar no banco" os dados ficam em dataSource.data (fluxo Manual),
        // então a fonte REST API ao vivo é ignorada aqui.
        const isRestApiPersisted = config?.restApi?.enabled && config?.restApi?.persistToDatabase;
        const isRestApiSource = config?.restApi?.enabled && !isRestApiPersisted && (restApiData.length > 0 || restApiLoading);
        const isGoogleSheetsSource = !isRestApiSource && config?.googleSheets?.enabled && googleSheetsData.length > 0;
        const tableData = isRestApiSource
          ? applyFilters(restApiData)
          : isGoogleSheetsSource
          ? applyFilters(googleSheetsData)
          : config?.sourceComponentId || config?.dynamicSourceField
          ? applyFilters(getComponentData())
          : applyFilters(config?.dataSource?.data || []);

        // Enriquecer colunas com dados de lookup
        const enrichedColumns = (config?.columns || []).map((col: any) => {
          if (col.type === "lookup" && col.lookupTable?.tableId) {
            // Buscar tabela referenciada
            const lookupTableComponent = allComponents.find(
              (c) => c.id === col.lookupTable.tableId,
            );
            if (lookupTableComponent) {
              return {
                ...col,
                lookupTable: {
                  ...col.lookupTable,
                  data: lookupTableComponent.config?.dataSource?.data || [],
                },
              };
            }
          }
          return col;
        });

        return (
          <>
            <TableWidget
              title={config?.title || "Tabela"}
              data={tableData}
              columns={enrichedColumns}
              pagination={config?.pagination}
              pageSize={config?.pageSize}
              striped={config?.striped}
              hoverable={config?.hoverable}
              compact={config?.compact}
              onSyncApi={
                isRestApiPersisted && onComponentChange
                  ? () => fetchAndApplyRestApi({ persist: true })
                  : undefined
              }
              syncing={restApiLoading}
              lastSyncedAt={config?.restApi?.lastSyncedAt}
              googleSheetsStatus={
                config?.restApi?.enabled
                  ? {
                      loading: restApiLoading,
                      error: restApiError,
                      connected: !restApiLoading && !restApiError && tableData.length > 0,
                      recordCount: tableData.length,
                    }
                  : config?.googleSheets?.enabled
                  ? {
                      loading: googleSheetsLoading,
                      error: googleSheetsError,
                      connected: !googleSheetsLoading && !googleSheetsError && googleSheetsData.length > 0,
                      recordCount: googleSheetsData.length,
                    }
                  : undefined
              }
              onAdd={onComponentChange ? () => {
                // Criar linha vazia com base nas colunas
                const emptyRow: any = {};
                (config?.columns || []).forEach((col: any) => {
                  if (col.type === "id") {
                    // Gerar próximo ID baseado no maior valor existente
                    const maxId = Math.max(
                      0,
                      ...tableData.map(
                        (row: any) => Number(row[col.field]) || 0,
                      ),
                    );
                    emptyRow[col.field] = maxId + 1;
                  } else if (
                    col.defaultValue !== undefined &&
                    col.defaultValue !== ""
                  ) {
                    // Usar valor padrão se definido
                    emptyRow[col.field] = col.defaultValue;
                  } else {
                    emptyRow[col.field] = "";
                  }
                });
                setEditingRow({
                  row: emptyRow,
                  index: -1,
                  componentId: component.id,
                  isGoogleSheets: isGoogleSheetsSource,
                });
              } : undefined}
              onEdit={onComponentChange ? (row, index) => {
                setEditingRow({ 
                  row, 
                  index, 
                  componentId: component.id,
                  isGoogleSheets: isGoogleSheetsSource,
                });
              } : undefined}
              onDelete={onComponentChange ? async (_row, index) => {
                if (confirm("Tem certeza que deseja deletar esta linha?")) {
                  if (isGoogleSheetsSource) {
                    // Deletar do Google Sheets (usa Service Account do servidor)
                    const { deleteSheetRow } = await import('../services/google-sheets.service');
                    
                    try {
                      const sheetName = config?.googleSheets?.range?.split('!')[0] || 'Sheet1';
                      await deleteSheetRow(
                        resolvedSpreadsheetId!,
                        sheetName,
                        index
                      );
                      
                      // Recarregar dados
                      const { fetchSheetData } = await import('../services/google-sheets.service');
                      const { rows } = await fetchSheetData(
                        resolvedSpreadsheetId!,
                        config?.googleSheets?.range || 'Sheet1'
                      );
                      
                      // Atualizar cache compartilhado
                      googleSheetsCache.set(
                        resolvedSpreadsheetId!,
                        config?.googleSheets?.range || 'Sheet1',
                        rows,
                        [] // headers não mudaram
                      );
                      
                      setGoogleSheetsData(rows);
                    } catch (error: any) {
                      console.error('Erro ao deletar do Google Sheets:', error);
                      alert('Erro ao deletar: ' + error.message);
                    }
                  } else {
                    // Deletar dados locais
                    const newData = [...tableData];
                    newData.splice(index, 1);

                    if (onComponentChange) {
                      onComponentChange(component.id, {
                        ...config,
                        dataSource: {
                          ...config?.dataSource,
                          data: newData,
                        },
                      });
                    }
                  }
                }
              } : undefined}
            />
            <RowEditModal
              isOpen={editingRow?.componentId === component.id}
              onClose={() => setEditingRow(null)}
              onSave={async (editedRow) => {
                if (editingRow) {
                  // Processar colunas tableLink: criar página automaticamente ao adicionar nova linha
                  let finalRow = { ...editedRow };
                  if (editingRow.index === -1 && !editingRow.isGoogleSheets && currentWorkspace?.id) {
                    const tableLinkCols = (config?.columns || []).filter(
                      (col: any) => col.type === 'tableLink'
                    );
                    for (const col of tableLinkCols) {
                      const val = finalRow[col.field];
                      if (!val || typeof val !== 'object' || !val.sheetsUrl || val.pageSlug) continue;
                      try {
                        const spreadsheetId = extractSheetId(val.sheetsUrl);
                        if (!spreadsheetId) continue;

                        // Descobre o nome real da primeira aba da planilha em vez de
                        // assumir "Sheet1" (que pode não existir, ex: planilhas em
                        // português usam "Página1" ou o usuário renomeou a aba).
                        let sheetRange = 'Sheet1';
                        try {
                          const sheetNames = await fetchSheetNames(spreadsheetId);
                          if (sheetNames.length > 0) sheetRange = sheetNames[0];
                        } catch {
                          // Mantém o fallback 'Sheet1' se não conseguir listar as abas
                        }

                        let columns: any[] = [];
                        try {
                          const { headers, rows } = await fetchSheetData(spreadsheetId, sheetRange);
                          columns = headers.map((h: string) => {
                            const field = h.toLowerCase().replace(/\s+/g, '_');
                            return { field, label: h, type: inferColumnType(field, rows), visible: true };
                          });
                          // Pré-popula o cache local para a página filha já abrir sincronizada,
                          // sem precisar de uma primeira busca manual do usuário.
                          googleSheetsCache.set(spreadsheetId, sheetRange, rows, headers);
                        } catch {
                          // Prosseguir sem colunas se a busca falhar
                        }
                        const baseSlug = (val.name || 'planilha')
                          .toLowerCase()
                          .normalize('NFD')
                          .replace(/[̀-ͯ]/g, '')
                          .replace(/[^a-z0-9]+/g, '-')
                          .replace(/^-|-$/g, '');
                        const slug = `${baseSlug}-${Date.now()}`;
                        const pageName = val.name || col.label || 'Planilha';

                        // Resolver parentId da página atual via cache do React Query
                        const currentPageData: any = currentPageSlug
                          ? queryClient.getQueryData(['page', currentWorkspace.id, currentPageSlug])
                          : undefined;
                        const parentId: string | undefined = currentPageData?.id;

                        // 1. Criar integração Google Sheets
                        const integration = await integrationsApi.create(currentWorkspace.id, {
                          name: pageName,
                          type: 'google_sheets',
                          config: { spreadsheetId },
                        });
                        // Adiciona no store local imediatamente — sem isso, resolveCompSpreadsheetId()
                        // não encontra a integração recém-criada até algo mais revalidar a query
                        // ['integrations', ...] (ex: abrir a config de um componente), e a tabela
                        // da página nova fica sem sincronizar até lá.
                        addIntegration(integration);
                        queryClient.invalidateQueries({ queryKey: ['integrations', currentWorkspace.id] });

                        // 2. Criar página filha com a tabela vinculada à integração
                        const newPage = await pageApi.create(currentWorkspace.id, {
                          name: pageName,
                          slug,
                          icon: '📊',
                          ...(parentId ? { parentId } : {}),
                          components: [{
                            id: `table-${Date.now()}`,
                            type: 'table',
                            position: { x: 0, y: 0, w: 48, h: 15 },
                            config: {
                              title: pageName,
                              columns,
                              googleSheets: { enabled: true, integrationId: integration.id, range: sheetRange },
                              dataSource: { type: 'static', data: [] },
                            },
                          }],
                          layout: {},
                        });
                        queryClient.invalidateQueries({ queryKey: ['pages', currentWorkspace.id] });
                        finalRow = { ...finalRow, [col.field]: { ...val, pageSlug: newPage.slug } };
                      } catch (error: any) {
                        console.error('Erro ao criar página para tableLink:', error);
                        alert('Erro ao criar página: ' + error.message);
                      }
                    }
                  }

                  if (editingRow.isGoogleSheets) {
                    // Salvar no Google Sheets (usa Service Account do servidor)
                    const {
                      appendSheetRow,
                      updateSheetRow,
                      fetchSheetData: fetchSheet
                    } = await import('../services/google-sheets.service');

                    try {
                      const spreadsheetId = resolvedSpreadsheetId!;
                      const range = config?.googleSheets?.range || 'Sheet1';
                      const headers = (config?.columns || []).map((col: any) => col.label);

                      if (editingRow.index === -1) {
                        // Adicionar nova linha
                        const rowArray = (config?.columns || []).map((col: any) => {
                          return editedRow[col.field] !== undefined ? editedRow[col.field] : '';
                        });

                        await appendSheetRow(spreadsheetId, range, rowArray);
                      } else {
                        // Atualizar linha existente
                        await updateSheetRow(
                          spreadsheetId,
                          range,
                          editingRow.index,
                          editedRow,
                          headers
                        );
                      }

                      // Recarregar dados do Google Sheets
                      const { rows } = await fetchSheet(spreadsheetId, range);

                      // Atualizar cache compartilhado
                      googleSheetsCache.set(
                        spreadsheetId,
                        range,
                        rows,
                        [] // headers não mudaram
                      );

                      setGoogleSheetsData(rows);

                    } catch (error: any) {
                      console.error('Erro ao salvar no Google Sheets:', error);
                      alert('Erro ao salvar: ' + error.message);
                    }
                  } else {
                    // Salvar dados locais (usando finalRow para incluir pageSlug gerado)
                    const newData = [...tableData];
                    if (editingRow.index === -1) {
                      // Adicionar nova linha
                      newData.push(finalRow);
                    } else {
                      // Editar linha existente
                      newData[editingRow.index] = finalRow;
                    }

                    if (onComponentChange) {
                      onComponentChange(component.id, {
                        ...config,
                        dataSource: {
                          ...config?.dataSource,
                          data: newData,
                        },
                      });
                    }
                  }
                }
                setEditingRow(null);
              }}
              row={editingRow?.row || {}}
              columns={enrichedColumns}
              allComponents={allComponents}
              title={
                editingRow?.index === -1 ? "Adicionar Linha" : "Editar Linha"
              }
            />
          </>
        );

      // Componente temporariamente desabilitado
      /*
      case 'relationship-table':
        const relationshipData = applyFilters(config?.dataSource?.data || []);
        
        // Buscar dados atualizados das tabelas de origem
        let leftTableData = config?.leftTable?.data || [];
        let rightTableData = config?.rightTable?.data || [];
        
        // Se temos IDs das tabelas, buscar dados atualizados de allComponents
        if (config?.leftTable?.id) {
          const leftTableComponent = allComponents.find(c => c.id === config.leftTable.id);
          if (leftTableComponent) {
            leftTableData = leftTableComponent.config?.dataSource?.data || [];
          }
        }
        
        if (config?.rightTable?.id) {
          const rightTableComponent = allComponents.find(c => c.id === config.rightTable.id);
          if (rightTableComponent) {
            rightTableData = rightTableComponent.config?.dataSource?.data || [];
          }
        }
        
        return null;
        return (
          <RelationshipTableWidget
            title={config?.title || 'Relacionamentos'}
            data={relationshipData}
            leftTable={config?.leftTable ? {
              ...config.leftTable,
              data: leftTableData,
            } : { id: '', name: 'Tabela A', data: [], keyField: 'id', displayField: 'name' }}
            rightTable={config?.rightTable ? {
              ...config.rightTable,
              data: rightTableData,
            } : { id: '', name: 'Tabela B', data: [], keyField: 'id', displayField: 'name' }}
            leftKeyField={config?.leftKeyField || 'leftId'}
            rightKeyField={config?.rightKeyField || 'rightId'}
            columns={config?.columns || []}
            striped={config?.striped}
            hoverable={config?.hoverable}
            onDelete={(_row, index) => {
              if (confirm('Tem certeza que deseja deletar este relacionamento?')) {
                const newData = [...relationshipData];
                newData.splice(index, 1);
                
                if (onComponentChange) {
                  onComponentChange(component.id, {
                    ...config,
                    dataSource: {
                      ...config?.dataSource,
                      data: newData,
                    },
                  });
                }
              }
            }}
          />
        );
        */

      case "list":
        const listData = config?.sourceComponentId || config?.dynamicSourceField
          ? getComponentData()
          : config?.dataSource?.data || [];
        return (
          <ListWidget
            title={config?.title || "Lista"}
            items={listData}
          />
        );

      case "text":
        return (
          <TextWidget
            content={config?.content || "Texto"}
            fontSize={config?.fontSize}
            textAlign={config?.textAlign}
          />
        );

      case "divider":
        return <Divider color={config?.style?.borderColor} />;

      case "button":
        return (
          <ButtonWidget
            label={config?.label || "Novo Registro"}
            onClick={onButtonClick ? () => onButtonClick(component.id) : undefined}
            disabled={!onButtonClick}
          />
        );

      case "kanban":
        return (
          <KanbanWidget
            title={config?.title}
            columns={config?.columns || []}
            onCardsChange={onComponentChange ? (newColumns) => {
              onComponentChange(component.id, {
                ...config,
                columns: newColumns,
              });
            } : undefined}
          />
        );

      case "filter": {
        const filterPreset = config?.preset || "custom";

        // Resolver opções para modo shared (workspace filter groups)
        let resolvedOptions = config?.options;
        let resolvedField = config?.field;
        if (config?.optionsSource === "shared" && config?.sharedFilterGroupId) {
          const fg = getFilterGroupById(config.sharedFilterGroupId);
          if (fg) {
            resolvedOptions = (fg.groups as { id: string; name: string; values: string[] }[]).map(
              (g) => ({ label: g.name, value: g.name })
            );
            // Para filtros compartilhados, o campo é sempre o sourceColumn do grupo —
            // nunca depende do config.field salvo no componente (pode estar desatualizado)
            resolvedField = fg.dataSource?.sourceColumn || fg.field || resolvedField;
          }
        }

        // Modos "table"/"linkedTable": recalcula as opções a partir dos dados atuais
        // da tabela fonte, para não depender de clicar em "Atualizar" na config.
        const liveTableOptions = resolveTableBasedFilterOptions(config);
        if (liveTableOptions !== undefined) {
          resolvedOptions = liveTableOptions;
        }

        return (
          <FilterWidget
            preset={filterPreset}
            field={resolvedField}
            title={config?.title}
            options={resolvedOptions}
            activeFilters={activeFilters}
            onChange={onFilterChange}
            allowAll={config?.allowAll !== false}
            isCompact={isCompact}
          />
        );
      }

      case "calendar":
        return (
          <CalendarWidget
            title={config?.title || "Calendário"}
            events={config?.events || []}
            onEventsChange={onComponentChange ? (newEvents) => {
              onComponentChange(component.id, {
                ...config,
                events: newEvents,
              });
            } : undefined}
            viewMode={config?.viewMode || "month"}
            editable={!!onComponentChange && config?.editable !== false}
            googleCalendarEnabled={!!resolvedCalendarId}
            googleCalendarId={resolvedCalendarId || 'primary'}
          />
        );

      case "gantt":
        const ganttAvailableTables = allComponents
          .filter((c) => c.type === "table")
          .map((c) => {
            const pageName = c._pageName;
            const label = pageName
              ? `${c.config?.title || c.type} (Página: ${pageName})`
              : c.config?.title || `Tabela - ${c.id.substring(0, 8)}`;
            return { id: c.id, label };
          });
        const ganttFilterTableId = config?.filterField
          ? activeFilters[config.filterField]
          : undefined;
        return (
          <GanttWidget
            title={config?.title || "Cronograma"}
            tasks={config?.tasks || []}
            onTasksChange={onComponentChange ? (newTasks) => {
              onComponentChange(component.id, {
                ...config,
                tasks: newTasks,
              });
            } : undefined}
            editable={config?.editable !== false}
            viewMonths={config?.viewMonths || 2}
            defaultZoomMode={config?.defaultZoomMode || 'days'}
            showProgress={config?.showProgress !== false}
            availableTables={ganttAvailableTables}
            filterTableId={ganttFilterTableId}
          />
        );

      case "forms-manager":
        return (
          <FormsManagerWidget
            component={component}
            onComponentChange={onComponentChange}
          />
        );

      case "container":
        return (
          <ContainerWidget
            component={component}
            allComponents={allComponents}
            onButtonClick={onButtonClick}
            onComponentChange={onComponentChange}
            activeFilters={activeFilters}
            onFilterChange={onFilterChange}
            builder={{
              isBuilder,
              onEdit: onEditComponent,
              onDelete: onDeleteComponent,
              onDataEditClick,
              onColumnsEditClick,
              onChartFieldsEditClick,
              onKanbanColumnsEditClick,
              onAddChild,
              onMoveComponent,
            }}
          />
        );

      default: {
        const customWidget = getCustomWidget(type);
        if (customWidget) {
          const CustomComponent = customWidget.component;
          return (
            <CustomComponent
              title={config?.title}
              config={config}
              allComponents={allComponents}
              activeFilters={activeFilters}
              isCompact={isCompact}
            />
          );
        }
        return (
          <Card>
            <CardContent className="p-6">
              <p className="text-muted-foreground">
                Componente tipo "{type}" não implementado
              </p>
            </CardContent>
          </Card>
        );
      }
    }
  };

  return <div className="h-full">{renderComponent()}</div>;
}
